import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../../../components/ui/card";
import { Calendar, Check, Clock, Loader2, TriangleAlert } from "lucide-react";
import { DatePicker, TimePicker } from "antd";
import dayjs from "dayjs";
import { Button } from "../../../../../../components/ui/button";
import axios from "axios";
import { BaseUrlApi, ErrorMessage } from "../../../../../../lib/api";
import { toast } from "sonner";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { Skeleton } from "src/components/ui/skeleton";
// Extend Day.js with required plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export default function TimeStep({
  selectedTeacher,
  setCurrentStep,
  selectedDate,
  setSelectedDate,
  selectedSlots,
  setSelectedSlots,
  selectedPrice,
}) {
  const [availabilityData, setAvailabilityData] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(true);
  const [availabilityError, setAvailabilityError] = useState(null);
  const [pikeDate, setPikeDate] = useState(null);

  // Fetch availability when teacher changes
  useEffect(() => {
    if (!selectedTeacher) return;
    const getAvailability = async () => {
      try {
        const { data } = await axios.get(
          `${BaseUrlApi}/teacher_availability/${selectedTeacher.user.id}`
        );
        setAvailabilityData(data.data);
      } catch (error) {
        setAvailabilityError(
          "Failed to load teacher availability. Please try again later."
        );
        toast("Error", { description: error.message || "Unknown error" });
      } finally {
        setLoadingAvailability(false);
      }
    };
    getAvailability();
  }, [selectedTeacher]);

  // When date changes
  const handleDateChange = (date) => {
    setSelectedDate(dayjs(date));
    setPikeDate(dayjs(date));
  };

  // Compute dates teacher is available
  const getAvailableDates = () => {
    const dates = new Set();
    availabilityData.forEach((slot) => {
      const start = dayjs(slot.start_date);
      const end = slot.end_date ? dayjs(slot.end_date) : start;
      if (slot.is_recurring) {
        let curr = start;
        while (curr.isSameOrBefore(end, "day")) {
          if (
            curr.format("dddd").toLowerCase() === slot.day_of_week.toLowerCase()
          ) {
            dates.add(curr.format("YYYY-MM-DD"));
          }
          curr = curr.add(1, "day");
        }
      } else {
        dates.add(start.format("YYYY-MM-DD"));
      }
    });
    return Array.from(dates);
  };

  // Filter slots for the selected date
  const getAvailabilityForDate = (date) => {
    if (!date) return [];
    const sel = dayjs(date);
    return availabilityData.filter((slot) => {
      const start = dayjs(slot.start_date);
      const end = slot.end_date ? dayjs(slot.end_date) : start;
      const inRange =
        sel.isSameOrAfter(start, "day") && sel.isSameOrBefore(end, "day");
      if (slot.is_recurring && inRange) {
        return (
          sel.format("dddd").toLowerCase() === slot.day_of_week.toLowerCase()
        );
      }
      return inRange;
    });
  };

  // Build allowed hour/minute ranges
  const computeAllowedRanges = () => {
    const ranges = {};
    getAvailabilityForDate(selectedDate).forEach((slot) => {
      const [h0, m0] = slot.start_time.split(":").map(Number);
      const [h1, m1] = slot.end_time.split(":").map(Number);
      for (let h = h0; h <= h1; h++) {
        const min = h === h0 ? m0 : 0;
        const max = h === h1 ? m1 : 59;
        if (!ranges[h]) ranges[h] = [min, max];
        else {
          ranges[h] = [
            Math.min(ranges[h][0], min),
            Math.max(ranges[h][1], max),
          ];
        }
      }
    });
    return ranges;
  };

  // Disable unavailable or already-picked hours/minutes
  const customDisabledTime = () => {
    const allowed = computeAllowedRanges();
    const allNums = Array.from({ length: 60 }, (_, i) => i);
    const allHours = Array.from({ length: 24 }, (_, i) => i);

    // Block hours outside availability
    let disabledHours = allHours.filter((h) => !allowed[h]);
    // Also block hours already booked on this date
    const bookedHours = selectedSlots
      ?.filter((s) => s.date === selectedDate?.format("YYYY-MM-DD"))
      .map((s) => Number(s.start_time.split(":")[0]));
    disabledHours = Array.from(new Set([...disabledHours, ...bookedHours]));

    // Compute minute offsets for hours following a booked slot
    const nextHourOffsets = {};
    selectedSlots.forEach((s) => {
      if (s.date === selectedDate?.format("YYYY-MM-DD")) {
        const [h, m] = s.start_time.split(":").map(Number);
        const nextH = h + 1;
        nextHourOffsets[nextH] = Math.max(nextHourOffsets[nextH] || 0, m);
      }
    });

    return {
      disabledHours: () => disabledHours,
      disabledMinutes: (hour) => {
        if (!allowed[hour] || disabledHours.includes(hour)) return allNums;
        const [min, max] = allowed[hour];
        const offset = nextHourOffsets[hour] || 0;
        const effectiveMin = Math.max(min, offset);
        return allNums.filter((m) => m < effectiveMin || m > max);
      },
    };
  };

  // Identify which availability slot a time falls in
  const getMatchingAvailabilityId = (time) => {
    if (!time) return null;
    const base = "1970-01-01";
    const picked = dayjs(`${base} ${time.format("HH:mm")}`, "YYYY-MM-DD HH:mm");
    for (const slot of getAvailabilityForDate(selectedDate)) {
      const start = dayjs(`${base} ${slot.start_time}`, "YYYY-MM-DD HH:mm");
      const end = dayjs(`${base} ${slot.end_time}`, "YYYY-MM-DD HH:mm");
      if (
        picked.isSameOrAfter(start, "minute") &&
        picked.isSameOrBefore(end, "minute")
      ) {
        return slot.id;
      }
    }
    return null;
  };

  // When picking a time, add slot and clear the date selection
  const handleTimeChange = (time) => {
    const availabilityId = getMatchingAvailabilityId(time);
    if (!availabilityId) return;

    const newSlot = {
      teacher_availability_id: availabilityId,
      date: selectedDate?.format("YYYY-MM-DD"),
      start_time: time.format("HH:mm"),
    };

    const exists = selectedSlots.some(
      (s) =>
        s.teacher_availability_id === newSlot.teacher_availability_id &&
        s.date === newSlot.date &&
        s.start_time === newSlot.start_time
    );

    if (!exists) {
      setSelectedSlots([...selectedSlots, newSlot]);
      // remove the selected date so the user must pick another
      setSelectedDate(null);
      setPikeDate(null);
    }
  };

  const availableDates = getAvailableDates();

  return (
    <section className="mb-16">
      <h2 className="text-2xl font-bold mb-2">Select Date & Time</h2>
      <p className="text-muted-foreground mb-6">
        With teacher:{" "}
        <span className="font-medium text-blue-600">
          {selectedTeacher?.user?.name}
        </span>
      </p>

      {loadingAvailability ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
          <Skeleton className="w-full h-36" />
          <Skeleton className="w-full h-36" />
        </div>
      ) : availabilityError ? (
        <div className="p-4 rounded-xl border border-destructive bg-destructive/40 mb-4 text-destructive-foreground">
          {availabilityError}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
          {/* Date Picker */}
          <Card>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle className="flex items-center">
                <Calendar className="mr-2 h-5 w-5 text-blue-600" />
                <span>Select a date</span>
              </CardTitle>
              
              {selectedPrice.lesson_num === selectedSlots.length && (
                <div className="text-yellow-500 text-base !m-0 font-semibold flex items-center gap-1">
                  <span class="relative flex">
                    <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-yellow-400 opacity-75"></span>
                    <span class="relative ">
                      <TriangleAlert className="size-5" />
                    </span>
                  </span>
                  Max dates is {selectedPrice.lesson_num}
                </div>
              )}
            </CardHeader>
            <CardContent>
              <DatePicker
                className="p-2 w-full"
                onChange={handleDateChange}
                value={pikeDate}
                format="YYYY-MM-DD"
                disabledDate={(current) => {
                  const d = dayjs(current).format("YYYY-MM-DD");
                  return (
                    !availableDates.includes(d) ||
                    selectedSlots.some((s) => s.date === d)
                  );
                }}
                disabled={selectedPrice.lesson_num === selectedSlots.length}
              />
            </CardContent>
          </Card>

          {/* Time Picker */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Clock className="mr-2 h-5 w-5 text-blue-600" />
                <span>Select an available time</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TimePicker
                className="p-2 w-full"
                value={null}
                onChange={handleTimeChange}
                format="HH:mm"
                disabledTime={customDisabledTime}
                disabled={!selectedDate}
              />
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
        {/* Available Dates */}
        <Card className="mt-4 h-fit">
          <CardHeader className="pb-4">
            <CardTitle>Available Dates</CardTitle>
          </CardHeader>
          <CardContent>
            {/* Available Dates Sidebar */}
            <div className="overflow-y-auto">
              {/* <h3 className="font-medium mb-2">Available dates:</h3> */}
              <div className="text-sm text-muted-foreground ps-2">
                {availableDates.length ? (
                  <ul className="space-y-1">
                    {availableDates.map((d) => (
                      <li key={d} className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span>
                          {dayjs(d).format("YYYY-MM-DD")} (
                          {dayjs(d).format("dddd")})
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No available dates found.</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Selected Slots */}
        <Card className="mt-4">
          <CardHeader className="pb-4">
            <CardTitle>Selected Time Slots</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedSlots?.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                No times selected yet.
              </p>
            ) : (
              <ul className="space-y-2">
                {selectedSlots?.map((slot, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between border p-2 rounded"
                  >
                    <span>
                      {slot.date} @ {slot.start_time}
                    </span>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() =>
                        setSelectedSlots((prev) =>
                          prev.filter((_, idx) => idx !== i)
                        )
                      }
                    >
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Navigation */}
      <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(2)}
          className="w-full sm:w-fit"
        >
          Back to Pricing
        </Button>
        <Button
          disabled={selectedSlots?.length === 0}
          onClick={() => selectedSlots && setCurrentStep(4)}
          className="w-full sm:w-fit"
        >
          Continue to Confirmation
        </Button>
      </div>
    </section>
  );
}
